/**
 * Текст «Свойства» вложения для hover-tooltip (паритет Swing `PreviewBean.getBeanProperties()`,
 * без `code`/`version` в UI).
 *
 * @see swingui/.../PreviewBean.java#getBeanProperties
 * @see swingui/.../JJAttachmentPane.java — setToolTipText(bean.getBeanProperties())
 */

/** Минимальный контракт строки вложения (без импорта из `MFileInput.vue`). */
export type AttachmentTooltipRow = {
  file?: File
  fields?: Record<string, { value?: unknown; type?: string } | undefined>
}

export type AttachmentSignatureInfo = {
  fileName: string
  changer: string
  isElectronic: boolean
  isApproved: boolean
  verifyResultDescription: string
  emchdVerifyResultDescription: string
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function readTypedField(fields: Record<string, unknown> | undefined, keys: string[]): unknown {
  if (!fields) return undefined
  for (const key of keys) {
    const raw = fields[key]
    if (raw == null) continue
    if (typeof raw === 'object' && raw !== null && 'value' in raw) {
      return (raw as { value: unknown }).value
    }
    return raw
  }
  return undefined
}

function readStringField(fields: Record<string, unknown> | undefined, keys: string[]): string {
  const value = readTypedField(fields, keys)
  if (value == null || value === '') return ''
  return String(value).trim()
}

function readBooleanField(fields: Record<string, unknown> | undefined, keys: string[]): boolean {
  const value = readTypedField(fields, keys)
  if (value === true || value === 'true' || value === 1 || value === '1') return true
  return false
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDateValue(value: unknown): string {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleString()
  }
  const asNumber = Number(value)
  if (Number.isFinite(asNumber) && asNumber > 0) {
    const date = new Date(asNumber)
    if (!Number.isNaN(date.getTime())) return date.toLocaleString()
  }
  return String(value).trim()
}

function rowFields(file: AttachmentTooltipRow): Record<string, unknown> {
  return (file.fields ?? {}) as Record<string, unknown>
}

/** Имя файла из строки вложения. */
export function getAttachmentDisplayName(file: AttachmentTooltipRow): string {
  if (file.file instanceof File) return file.file.name
  return readStringField(rowFields(file), ['name', 'fileName']) || 'Unnamed'
}

/** Размер файла в байтах. */
export function getAttachmentSizeBytes(file: AttachmentTooltipRow): number {
  if (file.file instanceof File) return file.file.size
  const size = readTypedField(rowFields(file), ['size', '_size'])
  const n = Number(size)
  return Number.isFinite(n) ? n : 0
}

function extractSignatureRows(file: AttachmentTooltipRow): { fields?: Record<string, unknown> }[] {
  const tableValue = readTypedField(rowFields(file), ['signature_file_list'])
  if (!tableValue || typeof tableValue !== 'object') return []
  const rows = (tableValue as { rows?: { fields?: Record<string, unknown> }[] }).rows
  return Array.isArray(rows) ? rows : []
}

/** Подписи из вложенной таблицы `signature_file_list`. */
export function parseAttachmentSignatures(file: AttachmentTooltipRow): AttachmentSignatureInfo[] {
  return extractSignatureRows(file).map((row) => {
    const fields = row.fields ?? {}
    return {
      fileName: readStringField(fields, ['signatureFileName', 'signature_file_name']),
      changer: readStringField(fields, [
        'signatureChanger',
        'signature_changer',
        'changer',
      ]),
      isElectronic: readBooleanField(fields, [
        'isElectronicSignature',
        'is_digital_signature',
        'isDigitalSignature',
      ]),
      isApproved: readBooleanField(fields, [
        'isApprovedSignature',
        'is_approved_signature',
      ]),
      verifyResultDescription: readStringField(fields, [
        'verifyResultDescription',
        'verify_result_description',
      ]),
      emchdVerifyResultDescription: readStringField(fields, [
        'emchdVerifyResultDescription',
        'emchd_verify_result_description',
      ]),
    }
  })
}

function appendLine(lines: string[], label: string, value: string) {
  const trimmed = value.trim()
  if (!trimmed) return
  lines.push(`${label}: ${trimmed}`)
}

function appendHtmlLine(parts: string[], label: string, value: string) {
  const trimmed = value.trim()
  if (!trimmed) return
  parts.push(`<div><b>${escapeHtml(label)}:</b> ${escapeHtml(trimmed)}</div>`)
}

/** Плоский текст для `title` / accessibility. */
export function buildAttachmentPropertiesPlainText(file: AttachmentTooltipRow): string {
  const lines: string[] = []
  const fields = rowFields(file)
  const name = getAttachmentDisplayName(file)
  if (name) lines.push(name)

  const pages = readStringField(fields, ['pages'])
  if (pages) appendLine(lines, 'Страниц', pages)

  const signatures = parseAttachmentSignatures(file)
  if (signatures.length) {
    const signers = signatures
      .map((s) => s.changer || s.fileName)
      .filter(Boolean)
      .join(', ')
    if (signers) appendLine(lines, 'Подписи', signers)
    const verify = signatures.find((s) => s.verifyResultDescription)?.verifyResultDescription
    if (verify) appendLine(lines, 'Результат проверки подписи', verify)
    const emchd = signatures.find((s) => s.emchdVerifyResultDescription)
      ?.emchdVerifyResultDescription
    if (emchd) appendLine(lines, 'Результат проверки МЧД', emchd)
  }

  const sizeLabel = formatFileSize(getAttachmentSizeBytes(file))
  if (sizeLabel) appendLine(lines, 'Размер', sizeLabel)

  const changer =
    readStringField(fields, ['changer']) ||
    readStringField(fields, ['_changer.login']) ||
    readStringField(fields, ['_changer.name'])
  if (changer) appendLine(lines, 'Изменил', changer)

  const versionDate = formatDateValue(readTypedField(fields, ['versionDate', 'version_date']))
  if (versionDate) appendLine(lines, 'Дата', versionDate)

  const versionComment = readStringField(fields, ['versionComment', 'version_comment'])
  if (versionComment) appendLine(lines, 'Комментарий', versionComment)

  return lines.join('\n')
}

/** HTML для hover-tooltip. */
export function buildAttachmentPropertiesHtml(file: AttachmentTooltipRow): string {
  const parts: string[] = []
  const fields = rowFields(file)
  const name = getAttachmentDisplayName(file)
  if (name) {
    parts.push(`<div class="attachment-props-tooltip__title">${escapeHtml(name)}</div>`)
  }

  const templateId = readStringField(fields, ['templateId', 'template_id'])
  if (templateId) appendHtmlLine(parts, 'ID шаблона', templateId)

  const pages = readStringField(fields, ['pages'])
  if (pages) appendHtmlLine(parts, 'Страниц', pages)

  const signatures = parseAttachmentSignatures(file)
  if (signatures.length) {
    const signers = signatures
      .map((s) => {
        const who = s.changer || s.fileName
        if (!who) return ''
        const flags: string[] = []
        if (s.isElectronic) flags.push('ЭП')
        if (s.isApproved) flags.push('утв.')
        return flags.length ? `${who} (${flags.join(', ')})` : who
      })
      .filter(Boolean)
      .join(', ')
    if (signers) appendHtmlLine(parts, 'Подписи', signers)

    for (const sig of signatures) {
      if (sig.verifyResultDescription) {
        appendHtmlLine(parts, 'Результат проверки подписи', sig.verifyResultDescription)
        break
      }
    }
    for (const sig of signatures) {
      if (sig.emchdVerifyResultDescription) {
        appendHtmlLine(parts, 'Результат проверки МЧД', sig.emchdVerifyResultDescription)
        break
      }
    }
  }

  const sizeLabel = formatFileSize(getAttachmentSizeBytes(file))
  if (sizeLabel) appendHtmlLine(parts, 'Размер', sizeLabel)

  parts.push('<hr class="attachment-props-tooltip__sep" />')

  const changer =
    readStringField(fields, ['changer']) ||
    readStringField(fields, ['_changer.login']) ||
    readStringField(fields, ['_changer.name'])
  if (changer) appendHtmlLine(parts, 'Изменил', changer)

  const versionDate = formatDateValue(readTypedField(fields, ['versionDate', 'version_date']))
  if (versionDate) appendHtmlLine(parts, 'Дата', versionDate)

  const versionComment = readStringField(fields, ['versionComment', 'version_comment'])
  if (versionComment) appendHtmlLine(parts, 'Комментарий', versionComment)

  if (!parts.length) return ''
  return `<div class="attachment-props-tooltip__body">${parts.join('')}</div>`
}
